import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { normalizeOperationColumns } from "@/views/components/ProTable";
import { Button, Card, Form, Input, Popconfirm, Typography } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

import request from "@/api";
import { useUserStore } from "@/views/stores";
import { TableSearchIcon } from "@/views/components/TableSearchIcon";
import type { SystemRole, SystemRolePageResult, SystemRoleQuery } from "./types";

export interface SystemRolePageProps {
    deleteRole?: (id: number | string) => Promise<void>;
    loadRoles?: (query: SystemRoleQuery) => Promise<SystemRolePageResult>;
    permissions?: string[];
}

const defaultQuery: SystemRoleQuery = { page: 1, size: 10, name: "" };

type RoleFilterValues = {
    name?: string;
};

/** 复用旧 Vue `/admin/role/paging` 接口的角色列表请求。 */
export async function loadSystemRoles(query: SystemRoleQuery): Promise<SystemRolePageResult> {
    const response = await request.service.get<SystemRolePageResult>("/admin/role/paging", { params: query });
    return response.data;
}

/** 复用旧 Vue 删除角色接口，保留服务层可替换的测试边界。 */
export async function deleteSystemRole(id: number | string): Promise<void> {
    await request.service.delete(`/admin/role/${id}`);
}

/** 系统管理-角色列表页面，先覆盖旧页面的查询结果展示主流程。 */
export function SystemRolePage({
    deleteRole = deleteSystemRole,
    loadRoles = loadSystemRoles,
    permissions
}: SystemRolePageProps) {
    const navigate = useNavigate();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const storeBanButton = useUserStore(state => state.banButton);
    const currentPermissions = permissions || storePermissions;
    // 保留 Vue v-auth 的特殊权限：封禁/提示权限会保留角色写操作入口。
    const canWrite = currentPermissions.some(permission =>
        ["ROLE_ROLE_W", "ROLE_BUTTON_BAN", "ROLE_BUTTON_TIP"].includes(permission)
    );
    const writeActionsDisabled = permissions ? currentPermissions.includes("ROLE_BUTTON_BAN") : storeBanButton;
    const [roles, setRoles] = useState<SystemRole[]>([]);
    const [total, setTotal] = useState(0);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [query, setQuery] = useState<SystemRoleQuery>(defaultQuery);
    const [filterForm] = Form.useForm<RoleFilterValues>();

    useEffect(() => {
        let disposed = false;
        void loadRoles(query)
            .then(result => {
                if (!disposed) {
                    setErrorMessage(null);
                    setRoles(result.list || []);
                    setTotal(result.total || 0);
                }
            })
            .catch(() => {
                if (!disposed) {
                    setErrorMessage("角色加载失败");
                    setRoles([]);
                    setTotal(0);
                }
            });
        return () => {
            disposed = true;
        };
    }, [loadRoles, query]);

    /** 删除成功后重新读取当前分页，保留旧 Vue ProTable 的刷新语义。 */
    const reloadRoles = async () => {
        const result = await loadRoles(query);
        // 刷新成功代表当前列表已恢复，清除上一次操作遗留的错误提示。
        setErrorMessage(null);
        setRoles(result.list || []);
        setTotal(result.total || 0);
    };

    const handleDelete = async (id: number | string | undefined) => {
        if (id == null) return;
        try {
            // 删除或刷新失败时保留当前列表，并给出可观察的 Vue 兼容错误反馈。
            await deleteRole(id);
            await reloadRoles();
        } catch {
            setErrorMessage("角色删除失败");
        }
    };

    // 将旧 Vue ProTable 的角色名称筛选转换为分页接口参数。
    const submitFilters = ({ name }: RoleFilterValues) => {
        setErrorMessage(null);
        setQuery({ ...defaultQuery, name: name || "" });
    };

    // 清空角色名称并恢复旧 Vue 首屏分页参数。
    const resetFilters = () => {
        filterForm.resetFields();
        setQuery(defaultQuery);
    };

    // 用 zhihao-ui-react 列契约承接 Vue 角色名称、说明和权限操作列。
    const columns: TableColumnProps<SystemRole>[] = [
        {
            key: "name",
            label: "角色名称",
            width: 260,
            prop: "name",
            render: ({ row }) => (
                <Button
                    className="px-0"
                    type="link"
                    onClick={() => void navigate(`/systemMangement/systemManage/role/detail?id=${row.id || ""}`)}
                >
                    {row.name || "--"}
                </Button>
            )
        },
        { key: "remark", label: "角色说明", prop: "remark", render: ({ row }) => row.remark || "--" },
        ...(canWrite
            ? [
                  {
                      key: "operation",
                      label: "操作",
                      fixed: "right" as const,
                      prop: "operation",
                      width: 180,
                      render: ({ row }: { row: SystemRole }) => (
                          <div className="flex gap-2">
                              <Button
                                  disabled={writeActionsDisabled}
                                  type="link"
                                  onClick={() =>
                                      void navigate(`/systemMangement/systemManage/role/edit?id=${row.id || ""}`)
                                  }
                              >
                                  编辑
                              </Button>
                              <Popconfirm
                                  cancelText="取消"
                                  okText="确定"
                                  title="确认删除该角色吗？"
                                  onConfirm={() => void handleDelete(row.id)}
                              >
                                  <Button disabled={writeActionsDisabled} type="link">
                                      删除
                                  </Button>
                              </Popconfirm>
                          </div>
                      )
                  }
              ]
            : [])
    ];

    return (
        <section className="flex h-full min-h-full flex-col" aria-label="system-role-page">
            <Card className="flex h-full flex-1 flex-col [&_.ant-card-body]:flex [&_.ant-card-body]:min-h-0 [&_.ant-card-body]:flex-1 [&_.ant-card-body]:flex-col">
                <ZhTable<SystemRole>
                    data={roles}
                    columns={normalizeOperationColumns(columns, roles)}
                    pagination={false}
                    rowKey={record => record.id || record.name || "role"}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Typography.Title className="mb-4 mt-0" level={4}>
                                角色管理
                            </Typography.Title>

                            {errorMessage ? (
                                <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                            ) : null}

                            {/* 新增按钮与旧 Vue 一样位于筛选表单首位，保持同一行紧凑布局。 */}
                            <Form
                                className="admin-table-filter-form mb-4"
                                form={filterForm}
                                layout="inline"
                                onFinish={values => submitFilters(values)}
                            >
                                {canWrite ? (
                                    <Button
                                        className="admin-table-primary-action"
                                        disabled={writeActionsDisabled}
                                        type="primary"
                                        onClick={() => void navigate("/systemMangement/systemManage/role/add")}
                                    >
                                        新增
                                    </Button>
                                ) : null}
                                <Form.Item noStyle name="name">
                                    {/* 对齐旧 Vue 角色列表筛选字段的 maxlength=32 契约。 */}
                                    <Input aria-label="角色名称" maxLength={32} placeholder="请输入角色名称" />
                                </Form.Item>
                                <Button
                                    aria-label="查询角色"
                                    htmlType="submit"
                                    icon={<TableSearchIcon name="search" />}
                                    type="primary"
                                >
                                    搜索
                                </Button>
                                <Button
                                    className="table-filter-reset-button"
                                    icon={<TableSearchIcon name="reset" />}
                                    onClick={resetFilters}
                                >
                                    重置
                                </Button>
                            </Form>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                pageSizeOptions={[10, 20, 30, 40, 50, 100]}
                                showSizeChanger
                                // 沿用 Vue 分页参数，切换页大小时回到第 1 页并保留名称查询。
                                onChange={(page, pageSize) =>
                                    setQuery(currentQuery => ({
                                        ...currentQuery,
                                        page: pageSize !== currentQuery.size ? 1 : page,
                                        size: pageSize
                                    }))
                                }
                            />
                        </div>
                    }
                />
            </Card>
        </section>
    );
}

export default SystemRolePage;
